import { CarHireSearchParams, City, HotelSearchParams, Itinerary } from "../types";
import { addDaysToDate } from "./Utils";
import { FaPlane, FaTrain, FaCarSide, FaShip, FaBus } from "react-icons/fa6";
import { IconType } from 'react-icons';



export const create_content_from_output = (itinObj: Itinerary) => {
    const text = [];
    text.push(`# ${itinObj.data.title}\n`)
    itinObj.data.trip_days.map((day: any, index: number) => {
      if(index ==0 && itinObj.data.departure_city){
        text.push(`Depart: ${itinObj.data.departure_city} > ${itinObj.data.arrive_city} \n`)
      }

      text.push(`${index + 1}. ${day.title}\n${day.description}`)

      if(index == itinObj.data.trip_days.length - 1 && itinObj.data.departure_city){
        text.push(`\nReturn: ${itinObj.data.arrive_city} > ${itinObj.data.departure_city}`)
      }
    });
    return text.join('\n')
  }


export const transportIcons: Record<string, IconType> = {
  flight: FaPlane,
  train: FaTrain,
  car: FaCarSide,
  carpool: FaCarSide,
  ferry: FaShip,
  bus: FaBus,
  ground: FaBus,
}

export const transportIconsNumber: Record<number, IconType> = {
  1: FaPlane,
  2: FaTrain,
  3: FaShip,
  4: FaBus,
  5: FaCarSide,
}

export const llmTextSeparator: string = "\n\n=========\n\n";


export function getTransportIcon(
  transport_mode: string | number,
  size: number = 14,
  className: string = ""
) {
  if (typeof transport_mode === "string") {
    const Icon = transportIcons[transport_mode];
    return <Icon size={size} className={className} />;
  } else {
    const Icon = transportIconsNumber[transport_mode];
    return <Icon size={size} className={className} />;
  }
} 


export function generateHotelSearches(
  hotel_cities: { location: number; trip_day_ids: number[] }[],
  departure_date: string, cities: Record<string, City>
): HotelSearchParams[] {
  let hotelSearches: HotelSearchParams[] = [];
  let idCounter = 1;

  for (const city of hotel_cities) {
    const { location, trip_day_ids } = city;
    const locationCity = cities[location];
    const sortedDays = [...trip_day_ids].sort((a, b) => a - b);

    let group: number[] = [];

    for (let i = 0; i < sortedDays.length; i++) {
      const currentDay = sortedDays[i];
      const prevDay = sortedDays[i - 1];

      if (group.length === 0 || currentDay === prevDay + 1) {
        group.push(currentDay);
      } else {
        hotelSearches.push({
          id: idCounter++,
          location: locationCity,
          checkin_date: addDaysToDate(departure_date, group[0] - 1),
          checkout_date: addDaysToDate(departure_date, group[group.length - 1]),
        });
        group = [currentDay];
      }
    }


    if (group.length) {
      hotelSearches.push({
        id: idCounter++,
        location: locationCity,
        checkin_date: addDaysToDate(departure_date, group[0] - 1),
        checkout_date: addDaysToDate(departure_date, group[group.length - 1] ),
      });
    }

  }

  return hotelSearches.sort((a, b) => new Date(a.checkin_date).getTime() - new Date(b.checkin_date).getTime());
}



export function generateCarHireSearches(
  rental_car: {pickup_location_id: number, dropoff_location_id: number, trip_day_ids: number[]}[],
  departure_date: string, cities: Record<string, City>
): CarHireSearchParams[] {
  let carhireSearches: CarHireSearchParams[] = [];
  let idCounter = 1;

  for (const rental of rental_car) {
    const { pickup_location_id, dropoff_location_id, trip_day_ids } = rental;
    const pickupCity = cities[pickup_location_id];
    const dropoffCity = cities[dropoff_location_id];
    const sortedDays = [...trip_day_ids].sort((a, b) => a - b);

    let group: number[] = [];

    for (let i = 0; i < sortedDays.length; i++) {
      const currentDay = sortedDays[i];
      const prevDay = sortedDays[i - 1];

      if (group.length === 0 || currentDay === prevDay + 1) {
        group.push(currentDay);
      } else {
        carhireSearches.push({
          id: idCounter++,
          pickup_city: pickupCity,
          dropoff_city: dropoffCity,
          pickup_date: addDaysToDate(departure_date, group[0] - 1),
          dropoff_date: addDaysToDate(departure_date, group[group.length - 1]),
        });
        group = [currentDay];
      }
    }


    if (group.length) {
      carhireSearches.push({
        id: idCounter++,
          pickup_city: pickupCity,
          dropoff_city: dropoffCity,
        pickup_date: addDaysToDate(departure_date, group[0] - 1),
        dropoff_date: addDaysToDate(departure_date, group[group.length - 1] ),
      });
    }

  }

  return carhireSearches.sort((a, b) => new Date(a.pickup_date).getTime() - new Date(b.pickup_date).getTime());
}




export function parseDateString(dateString: string) {
  const date = new Date(dateString);

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // mois commence à 0 => janvier = 0
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds()
  };
}


type Coordinates = {
  latitude: number;
  longitude: number;
};
type DistanceUnit = "km" | "mi";

export function formatCoords(coords: [number, number]): Coordinates {
  return {
    latitude: coords[0],
    longitude: coords[1]
  }
}

export function haversineDistance(coord1: Coordinates, coord2: Coordinates, unit: DistanceUnit = "km", round_digits: number = 2): number {
  const R_KM = 6371;       // Rayon de la Terre en kilomètres
  const R_MI = 3958.8;     // Rayon de la Terre en miles

  const toRad = (angle: number): number => (angle * Math.PI) / 180;

  const dLat = toRad(coord2.latitude - coord1.latitude);
  const dLon = toRad(coord2.longitude - coord1.longitude);

  const lat1 = toRad(coord1.latitude);
  const lat2 = toRad(coord2.latitude);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const radius = unit === "mi" ? R_MI : R_KM;

  const result = radius * c;

  return parseFloat(result.toFixed(round_digits));
}


